import {
  ChakraTheme,
  DeepPartial,
  extendTheme,
  theme as defaultTheme,
  ThemeExtension as ThemeExtension,
} from '@chakra-ui/react';
import { withProse } from '@nikolovlazar/chakra-ui-prose';

import { themeColors } from '../components/uiold/Constants';

type MergeObjects<T extends ObjectOf<unknown>, U extends ObjectOf<unknown>> = {
  [K in keyof T | keyof U]: K extends keyof T
    ? K extends keyof U
      ? Expand<MergeDeep<T[K], U[K]>>
      : T[K]
    : K extends keyof U
    ? U[K]
    : never;
};

type MergeDeep<T, U> = T extends ObjectOf<unknown>
  ? U extends ObjectOf<unknown>
    ? MergeObjects<T, U>
    : U
  : U;

/**
 * A better typed version of extendTheme
 */
function extend<T extends ChakraTheme, U extends DeepPartial<ChakraTheme>>(
  _base: T,
  extensions: U,
  ...other: Array<ThemeExtension>
): MergeDeep<T, U> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return extendTheme(extensions, ...other) as any;
}

export const theme = extend(
  defaultTheme,
  {
    styles: {
      global: {
        html: {
          // These values are normal defaults. I'm specifying them here to revert
          // the values specified by Chakra's global CSS Reset. See:
          // https://github.com/chakra-ui/chakra-ui/blob/@chakra-ui/css-reset@1.1.1/packages/css-reset/src/css-reset.tsx
          WebkitFontSmoothing: 'auto',
          MozOsxFontSmoothing: 'auto',
        },
      },
    },
    colors: {
      primaryBlue: {
        400: themeColors.blue,
      },
    },
    components: {
      Button: {
        variants: {
          primary: {
            backgroundColor: 'primaryBlue.400',
            color: 'white',
          },
          link: {
            color: 'primaryBlue.400',
            textDecoration: 'underline',
            fontWeight: 'normal',
          },
        },
      },
      Checkbox: {
        baseStyle: {
          control: {
            borderColor: 'gray.500',
          },
        },
      },
      Link: {
        baseStyle: {
          color: 'primaryBlue.400',
          textDecoration: 'underline',
        },
      },
    },
  },
  // The default Prose styles can be found here:
  // https://github.com/nikolovlazar/chakra-ui-prose/tree/@nikolovlazar/chakra-ui-prose@1.2.1/packages/chakra-ui-prose/src/theme.ts
  withProse({
    baseStyle: {
      h1: { mt: 0, mb: '0.67em' },
      h2: { mt: 0, mb: '0.83em' },
      h3: { mt: 0, mb: '1em' },
      h4: { mt: 0, mb: '1.33em' },
      p: { mt: 0, mb: '1em' },
      code: {
        fontWeight: 'normal',
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
        paddingLeft: '0.3em',
        paddingRight: '0.3em',
        '&::before, &::after': {
          content: '""',
        },
      },
      pre: {
        mt: 0,
        mb: '1em',
        whiteSpace: 'nowrap !important',
        code: {
          backgroundColor: 'transparent',
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
      blockquote: { mt: 0, mb: '1em' },
      'ul, ol': {
        '&>li::marker': {
          color: 'inherit',
        },
      },
      figure: { mt: 0, mb: '1em' },
      table: { mt: 0, mb: '1em' },
      a: {
        fontWeight: 'normal',
        color: 'primaryBlue.400',
        textDecoration: 'underline',
      },
      hr: {
        my: { base: '1em !important', md: '1em !important' },
      },
      // This next one is probably not necessary if using Chakra's global CSS
      // Reset (which is enabled by default). See:
      // https://github.com/chakra-ui/chakra-ui/blob/@chakra-ui/css-reset@1.1.1/packages/css-reset/src/css-reset.tsx
      img: { maxW: '100%' },
    },
  }),
);
